import React, { useEffect, useState } from "react";
import {
  Menu,
  MenuItem,
  Checkbox,
  Chip,
  IconButton,
  Box,
  Divider,
  Typography,
  TextField,
  Autocomplete,
  ListItemIcon,
  Button,
  createFilterOptions,
} from "@mui/material";
import {
  CheckBoxOutlineBlank,
  CheckBox,
  MoreHoriz,
  Check,
  Delete,
} from "@mui/icons-material";
import api from "../../../services/api";

const filter = createFilterOptions();
const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const colorChip = [
  "chip.main.pending",
  "chip.main.completed",
  "chip.main.inProgress",
  "chip.main.waitingReview",
  "chip.main.waitingReturn",
  "chip.main.arquive",
];

export default function TagsMenu({
  anchorEl,
  open,
  onClose,
  cliente,
  onSaved,
  onSaving,
  setSnackbar,
  styles,
  theme,
}) {
  const [tags, setTags] = useState([]);
  const [buscarTag, setBuscarTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Sub-menu to edit a tag
  const [anchorElEdit, setAnchorElEdit] = useState(null);
  const [handleTag, setHandleTag] = useState({ id: "", tagName: "", color: "" });
  const openEditMenu = Boolean(anchorElEdit);

  useEffect(() => {
    if (open) {
      loadTags();
      if (cliente && cliente.tagsCliente) {
        setSelectedTagIds(cliente.tagsCliente.map((t) => t.id));
      } else {
        setSelectedTagIds([]);
      }
    } else {
      setBuscarTag("");
      setAnchorElEdit(null);
    }
  }, [open, cliente]);

  const loadTags = async () => {
    setLoading(true);
    try {
      const response = await api.post("/interno/findMany/tagsCircuito", {
        type: "clienteGrupo",
      });
      setTags(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar tags globais", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTag = async (tagId, isChecked) => {
    const newSelectedIds = isChecked
      ? [...selectedTagIds, tagId]
      : selectedTagIds.filter((id) => id !== tagId);

    setSelectedTagIds(newSelectedIds);
    if (onSaving) onSaving(true);

    try {
      await api.post("/interno/upsert/clienteTags", {
        clienteId: cliente.id,
        tagIds: newSelectedIds,
      });
      await onSaved();
    } catch (error) {
      setSnackbar({ children: "Erro ao salvar tags do cliente", severity: "error" });
    } finally {
      if (onSaving) onSaving(false);
    }
  };

  const criarTag = async (tagName) => {
    if (!tagName.trim()) return;
    
    let min = Math.ceil(0);
    let max = Math.floor(6);
    let indexColorChip = Math.floor(Math.random() * (max - min)) + min;

    try {
      const response = await api.post("/interno/create/tagsCircuito", {
        tagName: tagName.trim(),
        color: colorChip[indexColorChip],
        type: "clienteGrupo",
      });

      const newTag = response.data.newTag || response.data;
      setTags((prev) => [...prev, newTag]);
      setBuscarTag("");
      setSnackbar({ children: "Tag criada com sucesso", severity: "success" });

      // Automatically associate the new tag to this client
      await handleToggleTag(newTag.id, true);
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao criar tag", severity: "error" });
    }
  };

  const editarTag = async () => {
    if (!handleTag.tagName.trim()) return;

    try {
      const response = await api.post("/interno/update/tagsCircuito", {
        id: handleTag.id,
        tagName: handleTag.tagName.trim(),
        color: handleTag.color,
      });

      setTags((prev) =>
        prev.map((t) => (t.id === handleTag.id ? { ...t, ...handleTag } : t)),
      );
      setSnackbar({ children: "Tag editada com sucesso", severity: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao editar tag", severity: "error" });
    } finally {
      setAnchorElEdit(null);
    }
  };

  const deletarTag = async () => {
    try {
      await api.post("/interno/delete/tagsCircuito", { id: handleTag.id });
      setTags((prev) => prev.filter((t) => t.id !== handleTag.id));
      setSelectedTagIds((prev) => prev.filter((id) => id !== handleTag.id));
      setSnackbar({ children: "Tag deletada", severity: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao deletar tag", severity: "error" });
    } finally {
      setAnchorElEdit(null);
    }
  };

  const filteredTags = tags.filter((t) =>
    t.tagName.toLowerCase().includes(buscarTag.toLowerCase())
  );

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
        sx={{ width: "450px" }}
      >
        <Autocomplete
          freeSolo
          size="small"
          inputValue={buscarTag}
          onInputChange={(event, value) => setBuscarTag(value)}
          clearIcon={null}
          filterOptions={(options, params) => {
            const { inputValue } = params;
            setBuscarTag(inputValue);
            return [];
          }}
          disableCloseOnSelect
          multiple
          onKeyDown={(event) => {
            event.stopPropagation();
            if (
              event.key === "Enter" &&
              buscarTag &&
              !filteredTags.some((t) => t.tagName === buscarTag)
            ) {
              criarTag(buscarTag);
            }
          }}
          options={[]}
          value={[]}
          style={{ width: 280, margin: "0 auto" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={tags.length !== 0 ? "" : "Adicione ou crie uma tag"}
            />
          )}
        />

        <Box>
          <Divider sx={{ margin: "10px 0" }} />
          <Box sx={{ ml: "10px" }}>
            <Typography sx={styles.textNoGrupo || { fontSize: "0.875rem", fontWeight: "bold" }}>
              Selecione ou crie uma opção
            </Typography>
          </Box>
        </Box>

        <Box sx={{ maxHeight: "200px", overflowY: "scroll", overflowX: "hidden" }}>
          {filteredTags.length === 0 && buscarTag === "" && (
            <Box sx={{ display: "flex", flexDirection: "column", p: 1, alignItems: "center" }}>
              <Typography sx={{ fontWeight: "bold", opacity: 0.5, fontSize: "0.8rem" }}>
                Cliente não possui tags
              </Typography>
              <Typography sx={{ fontWeight: "bold", opacity: 0.5, fontSize: "0.8rem" }}>
                Digite para criar uma tag
              </Typography>
            </Box>
          )}

          {filteredTags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            return (
              <Box style={{ width: 300 }} key={tag.id}>
                <MenuItem
                  onClick={(e) => {
                    handleToggleTag(tag.id, !isSelected);
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleTag(tag.id, e.target.checked);
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <Chip
                    size="small"
                    label={tag.tagName}
                    sx={{
                      ...(styles.chipGrupo || {
                        margin: "3px",
                        fontWeight: "bold",
                        fontSize: "0.7rem",
                        color: "#fff",
                      }),
                      bgcolor: tag.color,
                    }}
                  />
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      setHandleTag(tag);
                      setAnchorElEdit(event.currentTarget);
                    }}
                    sx={{ marginLeft: "auto" }}
                  >
                    <MoreHoriz fontSize="small" />
                  </IconButton>
                </MenuItem>
              </Box>
            );
          })}
        </Box>

        {buscarTag && !filteredTags.some((t) => t.tagName === buscarTag) && (
          <MenuItem
            style={{ width: 300 }}
            onClick={(e) => {
              criarTag(buscarTag);
              e.stopPropagation();
            }}
          >
            <Typography style={{ marginLeft: "20px" }}>
              Criar "{buscarTag}"
            </Typography>
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={anchorElEdit}
        open={openEditMenu}
        onClose={() => setAnchorElEdit(null)}
        sx={{ width: "350px", marginLeft: "15px", marginTop: "15px" }}
      >
        <Box sx={{ p: 1 }}>
          <Typography sx={styles.textNoGrupo || { fontWeight: "bold", fontSize: "0.875rem" }}>Nome</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TextField
            size="small"
            value={handleTag.tagName}
            sx={{ margin: "10px" }}
            onChange={(e) => {
              e.stopPropagation();
              setHandleTag((prev) => ({ ...prev, tagName: e.target.value }));
            }}
          />
        </Box>
        <Box>
          <Divider />
          <Button
            startIcon={<Delete color="error" sx={{ marginLeft: "25px" }} />}
            sx={{
              width: "100%",
              justifyContent: "start",
              padding: "10px 0",
              color: theme.palette.text.main,
              textTransform: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              deletarTag();
            }}
          >
            Deletar tag
          </Button>
          <Divider />
        </Box>
        <Box sx={{ p: 1 }}>
          <Typography sx={styles.textNoGrupo || { fontWeight: "bold", fontSize: "0.875rem" }}>Cor</Typography>
        </Box>

        <Box>
          {colorChip.map((color, index) => (
            <MenuItem
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setHandleTag((prev) => ({ ...prev, color }));
              }}
            >
              <Box
                sx={{
                  height: "15px",
                  width: "15px",
                  margin: "3px 10px",
                  backgroundColor: color,
                }}
              />
              <Typography>
                {index === 0
                  ? "Vermelho"
                  : index === 1
                  ? "Verde"
                  : index === 2
                  ? "Laranja"
                  : index === 3
                  ? "Azul"
                  : index === 4
                  ? "Roxo"
                  : "Cinza"}
              </Typography>
              {handleTag.color === color && (
                <ListItemIcon sx={{ marginLeft: "auto" }}>
                  <Check />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </Box>
        <Box sx={{ margin: "10px", display: "flex", justifyContent: "end", gap: 1 }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setAnchorElEdit(null);
            }}
            size="small"
          >
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              editarTag();
            }}
          >
            Salvar
          </Button>
        </Box>
      </Menu>
    </>
  );
}
